import type { MouseEvent } from 'react'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import TaActionButton from '../TaActionButton'
import MiniProgressDot from '../MiniProgressDot'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { runScore } from '../../services/QuestionActions'
import type { Question } from '../../types'
import { hasAnswerText } from '../../common/common'

interface Props {
  question: Question
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  testId?: string
  // When set, the button disables once a score already exists.
  // Used in surfaces where re-scoring isn't a default affordance —
  // the user has to go into the question's tab to explicitly rescore.
  disableIfPresent?: boolean
}

// Row-sized variant of GenerateScoringButton. Renders a MiniProgressDot
// showing the question's current estimated score, with a spinner overlay
// while a score run is in flight. Disables when there's no answer to
// score or the question's mid-stream. Click fires `runScore` for just
// this question.
export default function GenerateScoringMiniDot({
  question, onMouseEnter, onMouseLeave, testId, disableIfPresent,
}: Props) {
  const { updateQuestion, getCurrentProject } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)
  const queued = processing.isqueuedscoring
  const running = processing.isrunningscoring
  // Distinguish "not yet generated" from "generated as zero". The
  // app convention (mirrored in ScoreFixTab / BidQuestionScoringTab)
  // treats `scoreint >= 0` as scored — anything else (undefined,
  // -1 sentinel from a fresh / EMPTY_BIDEVAL) is "not generated"
  // and should leave the button in its click-me state (grey).
  // A real `0` flows through so the dot renders the bottom band (red).
  const rawScore = (question.bideval as any)?.scoreint
  const score: number | null = typeof rawScore === 'number' && rawScore >= 0
    ? rawScore
    : null
  const disabled =
    !hasAnswer
    || processing.isprocessingscoring
    || (disableIfPresent && score !== null)

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    const r = await runScore(question, project, updateQuestion)
    if (r.success) addToast('success', 'Score estimated')
    else addToast('error', r.msg ?? 'Failed to estimate score')
  }

  return (
    <TaActionButton
      title={queued ? 'Queued' : 'Estimate Score'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      running={running}
      queued={queued}
      iconSize={18}
      icon={<MiniProgressDot value={score} icon={<AssessmentOutlinedIcon sx={{ fontSize: 13 }} />} />}
      sx={{ p: 0.25, flexShrink: 0 }}
      testId={testId}
    />
  )
}
